import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import queryString from "query-string";

import DropdownMenu from "@ats/src/components/shared/DropdownMenu";
import AdminJobEditModal from "@ats/src/views/admin/AdminJobEditModal";

import { useOrgJobs } from "@shared/queryHooks/admin/useAdminJobs";
import { useModalContext } from "@shared/context/ModalContext";
import { getOptionLabel, adminInternalAdminStatusOptions } from "@ats/src/lib/lookups";

function AdminOrgJobs(props: any) {
  const { openModal, removeModal } = useModalContext();
  const { location } = props;
  const queryParams = queryString.parse(location?.search || "");
  const organizationId = queryParams.organizationId
    ? parseInt(queryParams.organizationId as string, 10)
    : null;

  const {
    data: orgJobsData,
    isLoading,
    isSuccess,
    error,
  } = useOrgJobs(organizationId);

  window.logger("%c[AdminOrgJobs] render", "color: #1976D2", {
    props,
    organizationId,
    orgJobsData,
    isLoading,
  });

  const handleOnClickEditJob = (job) => {
    const modal = <AdminJobEditModal job={job} onCancel={removeModal} />;
    openModal(modal);
  };

  const truncateDescription = (description: string, maxLength = 50) => {
    if (!description) return "---";
    // Strip HTML tags
    const stripped = description.replace(/<[^>]*>/g, "");
    if (stripped.length <= maxLength) return stripped;
    return stripped.substring(0, maxLength) + "...";
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "published":
        return "Published";
      case "draft":
        return "Draft";
      case "status_archived":
        return "Archived";
      default:
        return status;
    }
  };

  // Extract jobs from the response
  const jobs = orgJobsData?.items || [];
  
  // Sort jobs: published first, draft second, archived last
  const publishedJobs = jobs.filter((job) => job.status === "published");
  const draftJobs = jobs.filter((job) => job.status === "draft");
  const archivedJobs = jobs.filter((job) => job.status === "status_archived");
  const sortedJobs = [...publishedJobs, ...draftJobs, ...archivedJobs];
  
  // Get organization info from first job (all jobs belong to same org)
  const organization = sortedJobs[0]?.organization || null;

  const jobNodes = () => {
    if (!isSuccess || sortedJobs.length === 0) {
      return [];
    }

    return sortedJobs.map((job: any) => {
      return (
        <tr className="admin-job-list__item" key={job.id}>
          <td>{job.title}</td>
          <td>{job.id}</td>
          <td>{job.jobApplicationsCount || 0}</td>
          <td>
            <Styled.Tag status={job.status}>
              {getStatusLabel(job.status)}
            </Styled.Tag>
          </td>
          <td>{job.visible ? "Yes" : "No"}</td>
          <td>
            <Styled.Description title={job.description?.replace(/<[^>]*>/g, "")}>
              {truncateDescription(job.description)}
            </Styled.Description>
          </td>
          <td>
            <Styled.Tag status={job.internalAdminStatus}>
              {getOptionLabel(adminInternalAdminStatusOptions, job.internalAdminStatus)}
            </Styled.Tag>
          </td>
          <td>{job.organization?.owner?.email || "---"}</td>
          <td>{job.organization?.name || job.organizationName || "---"}</td>
          <td>{job.organizationId}</td>
          <td>
            <DropdownMenu icon="settings" label="Job actions">
              <button onClick={() => handleOnClickEditJob(job)}>Edit job</button>
            </DropdownMenu>
          </td>
        </tr>
      );
    });
  };

  const jobCount = sortedJobs.length;

  return (
    <Styled.Component>
      <Styled.Header>
        <h2>
          {organization?.name || `Organization ${organizationId}`} Jobs
          {isSuccess && <span>({jobCount} jobs found)</span>}
        </h2>
      </Styled.Header>

      {isLoading && <Styled.Loading>Loading...</Styled.Loading>}

      {!isLoading && isSuccess && jobCount === 0 && (
        <Styled.Empty>No jobs found for this organization.</Styled.Empty>
      )}

      {error && (
        <Styled.Empty>Error loading jobs: {error?.message || JSON.stringify(error)}</Styled.Empty>
      )}

      {!isLoading && !organizationId && (
        <Styled.Empty>Invalid organization ID.</Styled.Empty>
      )}

      {isSuccess && jobCount > 0 && (
        <Styled.Table>
          <tbody>
            <tr>
              <th>Job Title</th>
              <th>Job ID</th>
              <th>Applications</th>
              <th>Status</th>
              <th>Visible</th>
              <th>Description</th>
              <th>Admin Status</th>
              <th>Owner Email</th>
              <th>Org Name</th>
              <th>Org ID</th>
              <th />
            </tr>
            {jobNodes()}
          </tbody>
        </Styled.Table>
      )}
    </Styled.Component>
  );
}

export default AdminOrgJobs;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Component = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AdminOrgJobs;
    ${t.p(4)};
  `;
});

Styled.Header = styled.header((props) => {
  const t: any = props.theme;
  return css`
    label: AdminOrgJobs_Header;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    h2 {
      ${[t.mt(1), t.text.h3, t.mb(2)]};
      display: flex;
      align-items: center;
    }
    span {
      ${[t.ml(2), t.text.sm, t.text.secondary, t.text.normal]};
    }
  `;
});

Styled.HeaderControls = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AdminOrgJobs_HeaderControls;
    display: flex;
    align-items: flex-end;
    gap: ${t.spacing[4]};
  `;
});

Styled.Loading = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AdminOrgJobs_Loading;
    ${[t.py(8), t.text.center, t.text.secondary]};
  `;
});

Styled.Empty = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AdminOrgJobs_Empty;
    ${[t.py(8), t.text.center, t.text.secondary]};
  `;
});

Styled.Table = styled.table((props) => {
  const t: any = props.theme;
  return css`
    label: AdminOrgJobs_Table;
    border-collapse: collapse;
    width: 100%;

    th {
      ${[t.pb(2), t.text.xs, t.text.bold]};
      text-align: start;
      ${t.text.primary};
    }

    tr {
      border-bottom: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
      ${t.text.secondary};
    }

    tr:last-of-type {
      border-bottom: none;
    }

    svg {
      stroke: ${t.color.black};
    }

    td,
    th {
      ${[t.p(2)]};

      &:first-of-type {
        ${t.pl(0)}
      }

      &:last-of-type {
        ${t.pr(0)}
      }
    }
  `;
});

Styled.Tag = styled.div<{ status?: string }>((props) => {
  const t: any = props.theme;
  const status = props.status;

  let borderColor = t.dark ? t.color.gray[800] : t.color.gray[200];
  let textColor = t.dark ? t.color.gray[200] : t.color.gray[600];

  // Color coding based on status
  if (status === "published") {
    borderColor = t.color.gray[400];
    textColor = t.dark ? t.color.gray[100] : t.color.gray[700];
  } else if (status === "approved") {
    borderColor = t.color.gray[400];
    textColor = t.dark ? t.color.gray[100] : t.color.gray[700];
  } else if (status === "rejected" || status === "quarantined") {
    borderColor = t.color.red[400];
    textColor = t.color.red[600];
  }

  return css`
    label: AdminOrgJobs_Tag;
    ${[t.h(6), t.px(2), t.text.xs, t.rounded.xs]}
    display: inline-flex;
    align-items: center;
    color: ${textColor};
    border: 1px solid ${borderColor};
    line-height: 1.4;
    overflow: hidden;
    white-space: nowrap;
    span {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  `;
});

Styled.Description = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AdminOrgJobs_Description;
    ${[t.text.xs]};
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `;
});

